import React from 'react'

import { Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { RECOMMENDATION_ACTION, RecommendationAction } from './RecommendationAction'
import WrapperApproveTooltip from './WrapperApproveTooltip'
import WrapperReviewTooltip from './WrapperReviewTooltip'
import WrapperSuggestionTooltip from './WrapperSuggestionTooltip'

export interface RecommendationReason {
  overlaps?: {
    directReportEmployeeNames?: string[] | null
    teamEmployeeNames?: string[] | null
    totalReportEmployeeNames?: string[] | null
  }
  shiftsInfo?: Array<{
    id: string
    name?: string | null
    startAt: string
    endAt: string
    state?: string | null
  }> | null
  availableDays?: {
    daysLeft: string
    leaveTypeName: string
  } | null
}

export type ShiftInfo = { id: string; name?: string | null; startAt: string; endAt: string }

export function getRecommendationDescriptionRich(
  action: RecommendationAction,
  recommendationReason: RecommendationReason | '',
  employeeName: string,
  workable?: boolean
) {
  let directReports = 0
  let directReportsNames: string[] = []
  let teamOverlaps = 0
  let totalOverlaps = 0
  let shifts: string[] = []
  let showShifts = true

  let shiftsInfo:
    | Array<{
        id: string
        name?: string | null
        startAt: string
        endAt: string
        state?: string | null
      }>
    | null
    | undefined = null

  if (recommendationReason && typeof recommendationReason !== 'string') {
    directReports = Array.isArray(recommendationReason.overlaps?.directReportEmployeeNames)
      ? recommendationReason.overlaps.directReportEmployeeNames.length
      : 0
    directReportsNames = Array.isArray(recommendationReason.overlaps?.directReportEmployeeNames)
      ? (recommendationReason.overlaps.directReportEmployeeNames?.filter(
          (name): name is string => typeof name === 'string'
        ) ?? [])
      : []
    teamOverlaps = Array.isArray(recommendationReason.overlaps?.teamEmployeeNames)
      ? recommendationReason.overlaps.teamEmployeeNames.length
      : 0
    totalOverlaps = Array.isArray(recommendationReason.overlaps?.totalReportEmployeeNames)
      ? recommendationReason.overlaps.totalReportEmployeeNames.length
      : 0
    shiftsInfo = recommendationReason.shiftsInfo
    if (Array.isArray(shiftsInfo)) {
      shifts = shiftsInfo
        .filter((info) => !!info && typeof info.id === 'string')
        .map((info) => info.id)
    } else if (shiftsInfo === null) {
      showShifts = false
    }
  }

  const availableDays =
    recommendationReason &&
    typeof recommendationReason === 'object' &&
    'availableDays' in recommendationReason &&
    recommendationReason.availableDays != null
      ? Number(recommendationReason.availableDays.daysLeft)
      : undefined

  if (action === RECOMMENDATION_ACTION.APPROVE) {
    const teamOverlapsNames =
      recommendationReason &&
      typeof recommendationReason !== 'string' &&
      Array.isArray(recommendationReason.overlaps?.teamEmployeeNames)
        ? recommendationReason.overlaps.teamEmployeeNames.filter(
            (name): name is string => typeof name === 'string'
          )
        : null

    const totalOverlapsNames =
      recommendationReason &&
      typeof recommendationReason !== 'string' &&
      Array.isArray(recommendationReason.overlaps?.totalReportEmployeeNames)
        ? recommendationReason.overlaps.totalReportEmployeeNames.filter(
            (name): name is string => typeof name === 'string'
          )
        : null

    return (
      <div>
        <Text.Normal color="black" size="200">
          {i18n.t('time_off.approval_recommendation.action_approve_based_on')}
        </Text.Normal>{' '}
        <WrapperApproveTooltip
          onlyShifts
          mode="inline"
          labelOverride={i18n.t('time_off.approval_recommendation.employee_schedule', {
            authorName: employeeName,
          })}
          shiftsLabelOverride={i18n.t(
            'time_off.approval_recommendation.scheduled_work_tooltip_subtitle'
          )}
        />
        ,{' '}
        <WrapperApproveTooltip
          teamOverlaps={teamOverlaps}
          teamOverlapsNames={teamOverlapsNames}
          totalOverlaps={totalOverlaps}
          totalOverlapsNames={totalOverlapsNames}
          mode="inline"
          labelOverride={i18n.t('time_off.approval_recommendation.no_conflicts_with_colleagues')}
        />{' '}
        <Text.Normal color="black" size="200">
          {i18n.t('time_off.approval_recommendation.and')}
        </Text.Normal>{' '}
        <WrapperApproveTooltip
          availableDays={availableDays}
          mode="availableDays"
          labelOverride={i18n.t('time_off.approval_recommendation.available_days')}
        />
        .
      </div>
    )
  }

  if (action === RECOMMENDATION_ACTION.NO_RECOMMENDATION) {
    return (
      <div>{i18n.t('time_off.approval_recommendation.not_enough_information_description')}</div>
    )
  }
  if (action !== RECOMMENDATION_ACTION.REVIEW) return null

  // Review
  const lines: React.ReactNode[] = []
  // Overlaps (direct reports)
  if (directReports > 0) {
    const overlapLabel = i18n.tp('time_off.approval_recommendation.overlap_label', {
      count: directReports,
    })

    lines.push(
      <div key="directReportsLine">
        <WrapperReviewTooltip
          number={directReports}
          names={directReportsNames}
          label={directReports === 1 ? 'overlap' : 'overlaps'}
          labeli18n={overlapLabel}
        />{' '}
        <Text.Normal color="black" size="200">
          {workable
            ? i18n.t('time_off.approval_recommendation.overlap_with_direct_report_workable')
            : i18n.t('time_off.approval_recommendation.overlap_with_direct_report_absent')}
        </Text.Normal>
      </div>
    )
  }
  // Shifts
  if (showShifts && Array.isArray(shiftsInfo) && shiftsInfo.length > 0) {
    const shiftLabel = i18n.tp('time_off.approval_recommendation.shift_label', {
      count: shiftsInfo.length,
    })

    lines.push(
      <div key="shiftsLine">
        <WrapperReviewTooltip
          number={shiftsInfo.length}
          names={shiftsInfo}
          label={shifts.length === 1 ? 'shift' : 'shifts'}
          labeli18n={shiftLabel}
        />{' '}
        <Text.Normal color="black" size="200">
          {i18n.tp('time_off.approval_recommendation.shift_planned', {
            count: shiftsInfo.length,
          })}
        </Text.Normal>{' '}
      </div>
    )
  }
  // Always show Other validations
  lines.push(
    <div key="suggestionLine" style={{ marginTop: 8 }}>
      <span>
        <WrapperSuggestionTooltip
          directOverlaps={directReports}
          shifts={showShifts ? shifts.length : undefined}
          showShifts={showShifts}
          totalOverlaps={totalOverlaps}
          teamOverlaps={teamOverlaps}
          availableDays={availableDays}
        />
      </span>{' '}
      <Text.Normal color="black" size="200">
        {i18n.t('time_off.approval_recommendation.other_validations_no_conflicts')}
      </Text.Normal>
    </div>
  )
  return <>{lines}</>
}
